import { notification } from 'antd';
import { useFetchBillingInfoQuery, useUpdatePaymentMethodMutation } from '../service';
import { useCallback, useState } from 'react';
import { UpdatePaymentMethodParams } from '../types';

const initialState = {
    creditCardToken: '',
    threeDSecureActionToken: '',
};

export default function useUpdateAndProcessPaymentMethod() {
    const [pendingPaymentTokens, setPendingPaymentTokens] = useState(initialState);
    const { data: billingInfo } = useFetchBillingInfoQuery();

    const [updatePaymentMethodTrigger, { isLoading: isUpdatingPaymentMethod }] = useUpdatePaymentMethodMutation();

    const removePendingTokens = () => {
        setPendingPaymentTokens(initialState);
    };

    const updatePaymentMethod = useCallback(
        async (creditCardToken: string, threeDSecureActionResultToken?: string) => {
            if (!billingInfo) {
                return { error: 'Something went wrong. Please try again' };
            }

            const payload: UpdatePaymentMethodParams = {
                token_id: creditCardToken,
            };

            if (billingInfo.company) {
                payload.company = billingInfo.company;
            }

            if (billingInfo.vat_number) {
                payload.vat_number = billingInfo.vat_number;
            }

            if (threeDSecureActionResultToken) {
                payload.three_d_secure_action_result_token_id = threeDSecureActionResultToken;
            }

            try {
                const response = await updatePaymentMethodTrigger(payload).unwrap();

                if ('action_token_id' in response) {
                    setPendingPaymentTokens({ creditCardToken: response.token_id, threeDSecureActionToken: response.action_token_id });
                } else {
                    setPendingPaymentTokens(initialState);

                    if ('error' in response) {
                        return { error: response.error };
                    }

                    notification.success({ message: 'Success', description: 'Your payment method was successfully updated!' });
                }
            } catch (error) {
                setPendingPaymentTokens(initialState);
                return { error: 'We cannot update your payment method. Please try again or contact support!' };
            }
        },

        [billingInfo, updatePaymentMethodTrigger]
    );

    return {
        updatePaymentMethod,
        removePendingTokens,
        pendingPaymentTokens,
        isUpdatingPaymentMethod,
    };
}
